import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { api, useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Package, Plus, Pencil, Trash2, Search, Link2, ChevronRight, Eye, EyeOff, Upload, Download, Zap, X, CheckCircle, XCircle, AlertTriangle, History, RefreshCw, Lock, ScanBarcode, Tag, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { formatPHP } from '../lib/utils';
import UploadQRDialog from '../components/UploadQRDialog';
import { TotpVerifyDialog } from '../components/TotpVerifyDialog';
import CategorySelect from '../components/CategorySelect';
import StockInjectionDialog from '../components/StockInjectionDialog';
import CalcInput from '../components/CalcInput';
import { localTodayStr } from '../lib/dateFormat';

// ── Typo-tolerant product search (same engine as /sales-new) ─────────────────
// Bails early once distance exceeds maxDist, so it stays fast across 500+ SKUs.
function levenshteinAtMost(a, b, maxDist) {
  if (a === b) return 0;
  const la = a.length, lb = b.length;
  if (Math.abs(la - lb) > maxDist) return maxDist + 1;
  if (la === 0) return lb;
  if (lb === 0) return la;
  let prev = new Array(lb + 1);
  let curr = new Array(lb + 1);
  for (let j = 0; j <= lb; j++) prev[j] = j;
  for (let i = 1; i <= la; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= lb; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > maxDist) return maxDist + 1;
    [prev, curr] = [curr, prev];
  }
  return prev[lb];
}

export default function ProductsPage() {
  const navigate = useNavigate();
  const { currentBranch, user, hasPerm } = useAuth();
  const canViewCost = hasPerm('products', 'view_cost');
  const canEditCost = hasPerm('products', 'edit_cost');
  const canCreateProduct = hasPerm('products', 'create');
  const canEditProduct = hasPerm('products', 'edit');
  const canAdjustInventory = hasPerm('inventory', 'adjust');
  const [allProducts, setAllProducts] = useState([]);   // full live data from API
  const [filteredProducts, setFilteredProducts] = useState([]); // post search+filter
  const [total, setTotal] = useState(0);                 // total across all branches/filters
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [fuzzyHint, setFuzzyHint] = useState(null);      // {query, count} when typo-fallback fires
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('grouped');
  const [page, setPage] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [repackDialog, setRepackDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [repackParentIds, setRepackParentIds] = useState(new Set()); // parents that have at least one repack
  const [repackConfirmParent, setRepackConfirmParent] = useState(null); // pending confirm dialog
  const [exporting, setExporting] = useState(false);
  const LIMIT = 20;

  const [form, setForm] = useState({ sku: '', name: '', category: 'General', unit: 'Box', cost_price: 0, prices: {}, barcode: '', description: '', product_type: 'stockable', unit_of_measurement: 'Box', vat_class: 'vatable' });
  const [repackForm, setRepackForm] = useState({ name: '', unit: 'Sachet', units_per_parent: 1, cost_price: 0, prices: {}, barcode: '' });

  // ── Inventory Correction (admin) ────────────────────────────────────────
  const [correctionExpanded, setCorrectionExpanded] = useState(false);
  const [currentStock, setCurrentStock] = useState(null);
  // Units the catalog already uses, most-used first — the unit box suggests
  // them and warns on a look-alike ("Bags" when "BAG" exists). The server
  // snaps variants anyway; this just shows the user what will be stored.
  const [unitOptions, setUnitOptions] = useState([]);
  useEffect(() => {
    const load = () => api.get('/products/vocabulary/duplicates')
      .then(r => setUnitOptions(r.data?.unit?.values || []))
      .catch(() => {});
    load();
    // a merge in the cleanup dialog changes which spelling is "the" spelling
    window.addEventListener('product-vocabulary-changed', load);
    return () => window.removeEventListener('product-vocabulary-changed', load);
  }, []);
  const unitKey = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/[. ]+$/, '');
  const unitTwin = (typed) => {
    const k = unitKey(typed);
    if (!k || unitOptions.includes(String(typed).trim())) return '';
    const singular = k.length > 2 && k.endsWith('s') && !k.endsWith('ss') ? k.slice(0, -1) : k;
    return unitOptions.find(u => [k, singular, `${k}s`].includes(unitKey(u))) || '';
  };
  const [correctionQty, setCorrectionQty] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionHistory, setCorrectionHistory] = useState([]);
  const [totpOpen, setTotpOpen] = useState(false);
  const [pendingCorrection, setPendingCorrection] = useState(null); // { new_qty, reason }
  const [correctionSaving, setCorrectionSaving] = useState(false);
  const [corrUploadQROpen, setCorrUploadQROpen] = useState(false);
  const [corrUploadId, setCorrUploadId] = useState(null);
  const [barcodeGenerating, setBarcodeGenerating] = useState(false);

  // ── Stock Injection (admin-only, Iter 217b) ─────────────────────────────
  const [injectionOpen, setInjectionOpen] = useState(false);
  const [injectionProduct, setInjectionProduct] = useState(null);

  // ── Manage Categories ──────────────────────────────────────────────────────
  const [catMgmtOpen, setCatMgmtOpen] = useState(false);
  const [catMgmtList, setCatMgmtList] = useState([]);
  const [catNewName, setCatNewName] = useState('');
  const [catSaving, setCatSaving] = useState(false);

  const loadCatMgmt = () => {
    api.get('/products/categories').then(r => {
      setCatMgmtList(r.data || []);
      setCategories(r.data || []);
    }).catch(() => {});
  };

  const addCatMgmt = async () => {
    const name = catNewName.trim();
    if (!name) return;
    setCatSaving(true);
    try {
      await api.post('/products/categories', { name });
      setCatNewName('');
      loadCatMgmt();
      toast.success(`Category "${name}" added`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setCatSaving(false);
  };

  const deleteCatMgmt = async (cat) => {
    if (!window.confirm(`Delete category "${cat}"? This fails if products still use it.`)) return;
    try {
      await api.delete(`/products/categories/${encodeURIComponent(cat)}`);
      loadCatMgmt();
      toast.success(`"${cat}" deleted`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ── Batch Quick Repack ──────────────────────────────────────────────────────
  const [qrOpen, setQrOpen] = useState(false);
  const [qrRows, setQrRows] = useState([]);
  const [qrGenerating, setQrGenerating] = useState(false);
  const [qrResults, setQrResults] = useState(null); // null = not yet generated
  const searchTimers = useRef({});
  const pendingFocusRowId = useRef(null);
  const dropdownRefs = useRef({});

  // After a new row is added via Tab, focus its parent search input
  useEffect(() => {
    if (pendingFocusRowId.current) {
      const input = document.querySelector(`[data-testid="qr-parent-${pendingFocusRowId.current}"]`);
      if (input) { input.focus(); pendingFocusRowId.current = null; }
    }
  }, [qrRows]);

  const computeCapital = (parent, unitsPerParent, addOnCost) => {
    if (!parent) return 0;
    return Math.round((parent.cost_price / (unitsPerParent || 1) + (parseFloat(addOnCost) || 0)) * 100) / 100;
  };

  const newRow = () => ({
    id: Math.random().toString(36).slice(2),
    parentSearch: '', parentMatches: [], parent: null,
    activeSearchIndex: -1,
    repackName: '', unit: 'Pack',
    unitsPerParent: 1, addOnCost: 0,
    capital: 0,
    retailPrice: '',
    barcode: '',
    retailError: null, rowError: null,
    hasRepack: false,
  });

  const updateRow = (id, updates) =>
    setQrRows(rows => rows.map(r => r.id === id ? { ...r, ...updates } : r));

  const removeRow = (id) =>
    setQrRows(rows => rows.filter(r => r.id !== id));

  const searchParent = (rowId, query) => {
    updateRow(rowId, { parentSearch: query, parent: null, capital: 0, rowError: null });
    if (searchTimers.current[rowId]) clearTimeout(searchTimers.current[rowId]);
    if (!query || query.length < 1) { updateRow(rowId, { parentMatches: [] }); return; }
    searchTimers.current[rowId] = setTimeout(async () => {
      try {
        const res = await api.get('/products', { params: { search: query, is_repack: false, limit: 8 } });
        updateRow(rowId, { parentMatches: res.data.products || [] });
      } catch {}
    }, 180);
  };

  const selectParent = (rowId, p) => {
    const capital = computeCapital(p, 1, 0);
    const hasRepack = repackParentIds.has(p.id);
    // Detect: same parent already used by another row in this session
    const dupRow = qrRows.find(r => r.id !== rowId && r.parent?.id === p.id);
    updateRow(rowId, {
      parent: p, parentSearch: p.name, parentMatches: [],
      activeSearchIndex: -1,
      repackName: `R ${p.name}`,
      unitsPerParent: 1, addOnCost: 0, capital,
      retailPrice: '', retailError: null, rowError: null,
      hasRepack,
      dupInSession: !!dupRow,
    });
    // Auto-focus and select the repack name input so user can type immediately
    setTimeout(() => {
      const nameInput = document.querySelector(`[data-testid="qr-name-${rowId}"]`);
      if (nameInput) { nameInput.focus(); nameInput.select(); }
    }, 80);
    if (dupRow) {
      toast.warning(`"${p.name}" is already in this batch (above). Make sure you intended a second variant.`, { duration: 5000 });
    } else if (hasRepack) {
      toast.warning(`"${p.name}" already has a repack. You can still create another (e.g. a different size).`);
    }
  };

  const handleParentKeyDown = (e, row) => {
    const matches = row.parentMatches || [];
    if (!matches.length) {
      if (e.key === 'Escape') updateRow(row.id, { parentSearch: '', parentMatches: [], activeSearchIndex: -1 });
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.min((row.activeSearchIndex ?? -1) + 1, matches.length - 1);
      updateRow(row.id, { activeSearchIndex: next });
      // Scroll the active item into view inside the dropdown panel
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-qr-option="${row.id}-${next}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.max((row.activeSearchIndex ?? 0) - 1, 0);
      updateRow(row.id, { activeSearchIndex: next });
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-qr-option="${row.id}-${next}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const idx = row.activeSearchIndex ?? -1;
      if (idx >= 0 && matches[idx]) selectParent(row.id, matches[idx]);
      else if (matches.length === 1) selectParent(row.id, matches[0]);
    } else if (e.key === 'Escape') {
      updateRow(row.id, { parentMatches: [], activeSearchIndex: -1 });
    }
  };

  // onBlur validation for retail price — check against capital
  const handleRetailBlur = (rowId, value) => {
    const row = qrRows.find(r => r.id === rowId);
    if (!row || !value) return;
    const retail = parseFloat(value);
    if (isNaN(retail) || retail <= 0) return;
    if (row.capital > 0 && retail <= row.capital) {
      updateRow(rowId, { retailError: `Below capital ₱${row.capital.toFixed(2)}` });
    } else {
      updateRow(rowId, { retailError: null });
    }
  };

  const openQrModal = () => {
    setQrRows([newRow()]);
    setQrResults(null);
    setQrOpen(true);
  };

  const handleBatchGenerate = async () => {
    // Validate all rows — mark errors, block if any invalid
    let hasErrors = false;
    const validated = qrRows.map(row => {
      let rowError = null;
      let retailError = row.retailError;
      if (!row.parent) { rowError = 'Select a parent product'; hasErrors = true; }
      else if (!row.repackName.trim()) { rowError = 'Repack name required'; hasErrors = true; }
      else if (!row.retailPrice || parseFloat(row.retailPrice) <= 0) { rowError = 'Retail price required'; hasErrors = true; }
      else if (row.capital > 0 && parseFloat(row.retailPrice) <= row.capital) {
        retailError = `Below capital ₱${row.capital.toFixed(2)}`; rowError = 'Fix retail price'; hasErrors = true;
      }
      return { ...row, rowError, retailError };
    });

    if (hasErrors) {
      setQrRows(validated);
      toast.error('Fix the highlighted rows before generating');
      return;
    }

    if (!currentBranch?.id) {
      toast.error('Please select a branch first to set repack price');
      return;
    }

    setQrGenerating(true);
    const results = [];
    for (const row of qrRows) {
      try {
        const allPrices = { retail: parseFloat(row.retailPrice) };
        await api.post(`/products/${row.parent.id}/generate-repack`, {
          name: row.repackName, unit: row.unit,
          units_per_parent: row.unitsPerParent,
          add_on_cost: row.addOnCost || 0,
          prices: allPrices,
          branch_id: currentBranch.id,
          barcode: row.barcode || '',
        });
        results.push({ name: row.repackName, status: 'ok' });
      } catch (e) {
        results.push({ name: row.repackName, status: 'error', error: e.response?.data?.detail || 'Error' });
      }
    }
    setQrGenerating(false);
    setQrResults(results);
    fetchProducts();
  };

  const handleBulkGenerateBarcodes = async () => {
    setBarcodeGenerating(true);
    try {
      const res = await api.post('/products/generate-barcodes-bulk');
      const count = res.data.generated || 0;
      if (count > 0) {
        toast.success(`Generated barcodes for ${count} product(s)`);
        fetchProducts();
      } else {
        toast.info('All parent products already have barcodes');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate barcodes'); }
    setBarcodeGenerating(false);
  };

  // ── Name cache (sessionStorage) — paint search index instantly on revisit ──
  // Caches only id/name/sku/barcode — never stock or prices (always live).
  const nameCacheKey = `agribooks_prods_v2_${currentBranch?.id || 'all'}`;

  const fetchProducts = useCallback(async () => {
    setLoadingProducts(true);
    // 1) Pre-populate search index from name cache so typing works immediately
    try {
      const raw = sessionStorage.getItem(nameCacheKey);
      if (raw) {
        const { names } = JSON.parse(raw);
        if (Array.isArray(names) && names.length > 0) {
          setAllProducts(prev => prev.length === 0 ? names : prev);
        }
      }
    } catch {}

    // 2) Fetch full live data (stock, prices, cost — always fresh)
    try {
      const params = { limit: 5000, sort_by: sortBy };
      if (currentBranch?.id) params.branch_id = currentBranch.id;
      const res = await api.get('/products', { params });
      const products = res.data.products || [];
      setAllProducts(products);
      setTotal(res.data.total || products.length);
      // Cache just the search-index fields for next visit
      try {
        const names = products.map(p => ({
          id: p.id, name: p.name, sku: p.sku, barcode: p.barcode,
          is_repack: p.is_repack, parent_id: p.parent_id,
        }));
        sessionStorage.setItem(nameCacheKey, JSON.stringify({ names }));
      } catch {}
    } catch { toast.error('Failed to load products'); }
    setLoadingProducts(false);
  }, [sortBy, currentBranch, nameCacheKey]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Debounce search input — 150ms matches the /sales-new implementation
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 150);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page when search query or type filter changes
  useEffect(() => { setPage(0); }, [debouncedSearch, filter]);

  // Pre-computed search index — rebuilt only when allProducts changes, not per-keystroke
  const productIndex = useMemo(() => allProducts.map(p => {
    const name = (p.name || '').toLowerCase();
    const sku = (p.sku || '').toLowerCase();
    const barcode = (p.barcode || '').toLowerCase();
    const haystack = `${name} ${sku} ${barcode}`;
    return {
      p,
      name,
      haystack,
      nameWords: name.split(/[\s\-/,]+/).filter(Boolean),
      words: haystack.split(/[\s\-/,]+/).filter(Boolean),
    };
  }), [allProducts]);

  // Client-side filter + token/fuzzy search (same algorithm as /sales-new)
  useEffect(() => {
    // Step 1: apply type filter
    const typeFiltered = filter === 'parent'
      ? productIndex.filter(idx => !idx.p.is_repack)
      : filter === 'repack'
        ? productIndex.filter(idx => idx.p.is_repack)
        : productIndex;

    const q = (debouncedSearch || '').toLowerCase().trim();
    if (!q) {
      setFilteredProducts(typeFiltered.map(idx => idx.p));
      setFuzzyHint(null);
      return;
    }

    const tokens = q.split(/[\s\-/,]+/).map(t => t.trim()).filter(Boolean);
    if (!tokens.length) { setFilteredProducts(typeFiltered.map(idx => idx.p)); setFuzzyHint(null); return; }

    const isShortNumeric = t => /^[0-9]{1,3}$/.test(t);
    // Products management page: short numerics use substring match (looser than POS).
    // The strict prefix-of-name-word rule is POS-only (to prevent "Galimax 1" vs "Galimax 2" confusion).
    // Here we want "sulfate 50" to find "Ammonium Sulfate (50kg)" via the SKU or parenthetical weight.
    const tokenHits = (t, haystack, nameWords) => {
      if (isShortNumeric(t)) {
        return nameWords.some(w => w.startsWith(t)) || haystack.includes(t);
      }
      return haystack.includes(t);
    };

    // ── Strict pass ──────────────────────────────────────────────────────
    const strict = [];
    for (const idx of typeFiltered) {
      const { p, name, haystack, nameWords } = idx;
      if (!tokens.every(t => tokenHits(t, haystack, nameWords))) continue;
      let rank = 2;
      if (name.startsWith(q)) rank = 0;
      else if (name.includes(q)) rank = 1;
      strict.push({ p, rank, len: name.length });
    }
    if (strict.length > 0) {
      strict.sort((a, b) => a.rank - b.rank || a.len - b.len);
      setFilteredProducts(strict.map(m => m.p));
      setFuzzyHint(null);
      return;
    }

    // ── Fuzzy fallback (typo tolerance) ──────────────────────────────────
    const fuzzable = tokens.filter(t => t.length >= 4 && !/^[0-9]+$/.test(t) && !/^[0-9]+[a-z]+$/i.test(t));
    const exactRequired = tokens.filter(t => !fuzzable.includes(t));
    if (!fuzzable.length) { setFilteredProducts([]); setFuzzyHint(null); return; }

    const allowedDist = t => t.length >= 8 ? 2 : 1;
    const pool = typeFiltered.slice(0, 300);
    const fuzzy = [];
    for (const idx of pool) {
      const { p, haystack, nameWords, words } = idx;
      if (!exactRequired.every(t => tokenHits(t, haystack, nameWords))) continue;
      let totalEdits = 0, ok = true;
      for (const t of fuzzable) {
        if (haystack.includes(t)) continue;
        const limit = allowedDist(t);
        let best = Infinity;
        for (const w of words) {
          if (Math.abs(w.length - t.length) > limit) continue;
          const d = levenshteinAtMost(t, w, limit);
          if (d < best) best = d;
          if (best === 0) break;
        }
        if (best > limit) { ok = false; break; }
        totalEdits += best;
      }
      if (!ok) continue;
      fuzzy.push({ p, totalEdits, len: (p.name || '').length });
    }
    if (fuzzy.length > 0) {
      fuzzy.sort((a, b) => a.totalEdits - b.totalEdits || a.len - b.len);
      setFilteredProducts(fuzzy.map(m => m.p));
      setFuzzyHint({ query: debouncedSearch.trim(), count: fuzzy.length });
    } else {
      setFilteredProducts([]);
      setFuzzyHint(null);
    }
  }, [debouncedSearch, filter, productIndex]);
  useEffect(() => {
    api.get('/price-schemes').then(r => setSchemes(r.data)).catch(() => {});
    api.get('/products/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/products/export-csv', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agribooks_products_${localTodayStr()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Products exported');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    }
    setExporting(false);
  };

  // Load all repack parent IDs so we can show green/red indicator
  const refreshRepackIndicators = useCallback(async () => {
    try {
      const res = await api.get('/products', { params: { is_repack: true, limit: 5000 } });
      const ids = new Set((res.data.products || []).filter(p => p.parent_id).map(p => p.parent_id));
      setRepackParentIds(ids);
    } catch {}
  }, []);
  useEffect(() => { refreshRepackIndicators(); }, [refreshRepackIndicators]);

  const openCreate = (prefillName = '') => {
    setEditing(null);
    setForm({ sku: '', name: prefillName, category: categories[0] || '', unit: 'Box', cost_price: 0, prices: {}, barcode: '', description: '', product_type: 'stockable', starting_inventory: 0, vat_class: 'vatable' });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ sku: p.sku, name: p.name, category: p.category, unit: p.unit, cost_price: p.cost_price, prices: p.prices || {}, barcode: p.barcode || '', description: p.description || '', vat_class: p.vat_class || 'vatable' });
    // Reset correction panel
    setCorrectionExpanded(false);
    setCorrectionQty('');
    setCorrectionReason('');
    setCorrectionHistory([]);
    setCurrentStock(null);
    setDialogOpen(true);
  };

  const loadStockForEdit = useCallback(async (product) => {
    if (!product || !currentBranch) return;
    try {
      const res = await api.get('/inventory', {
        params: { search: product.sku, branch_id: currentBranch.id, limit: 1 }
      });
      const item = (res.data.items || []).find(i => i.id === product.id);
      const stock = item ? (item.branch_stock?.[currentBranch.id] ?? item.total_stock ?? 0) : 0;
      setCurrentStock(stock);
    } catch { setCurrentStock(0); }
    // Load correction history
    try {
      const hist = await api.get(`/inventory/corrections/${product.id}`);
      setCorrectionHistory(hist.data || []);
    } catch {}
  }, [currentBranch]);

  useEffect(() => {
    if (correctionExpanded && editing) loadStockForEdit(editing);
  }, [correctionExpanded, editing, loadStockForEdit]);

  const submitCorrection = async (verifiedBy, authMode, correctionData) => {
    const correction = correctionData || pendingCorrection;
    if (!correction) return;
    setCorrectionSaving(true);
    try {
      const res = await api.post('/inventory/admin-adjust', {
        product_id: editing.id,
        branch_id: currentBranch.id,
        new_quantity: correction.new_qty,
        reason: correction.reason,
        verified_by: verifiedBy,
        auth_mode: authMode,
      });
      toast.success(`Stock corrected: ${res.data.old_quantity} → ${res.data.new_quantity}`);
      setCurrentStock(res.data.new_quantity);
      setCorrectionQty('');
      setCorrectionReason('');
      setPendingCorrection(null);
      // Refresh history
      const hist = await api.get(`/inventory/corrections/${editing.id}`);
      setCorrectionHistory(hist.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Correction failed'); }
    setCorrectionSaving(false);
  };

  const handleCorrectionSubmit = () => {
    const qty = parseFloat(correctionQty);
    if (isNaN(qty) || qty < 0) { toast.error('Enter a valid quantity (0 or more)'); return; }
    if (!correctionReason.trim()) { toast.error('Reason is required'); return; }
    const correction = { new_qty: qty, reason: correctionReason.trim() };
    if (user?.role === 'admin') {
      // Admin submits directly — pass correction data to avoid React state batching issue
      submitCorrection(user.full_name || user.username, 'direct_admin', correction);
    } else {
      // Non-admin: require TOTP verification
      setPendingCorrection(correction);
      setTotpOpen(true);
    }
  };

  const openRepack = (p) => {
    if (repackParentIds.has(p.id)) {
      // Already has repacks — show confirmation first
      setRepackConfirmParent(p);
      return;
    }
    // No repacks yet — open directly
    _launchRepackDialog(p);
  };

  const _launchRepackDialog = (p) => {
    if (!currentBranch?.id) {
      toast.error('Please select a branch first to set repack price');
      return;
    }
    setSelectedParent(p);
    const units = 1;
    const branchCost = Number(p.branch_cost_price ?? p.cost_price ?? 0);
    const globalCost = Number(p.cost_price ?? 0);
    const autoCost = branchCost > 0 ? Math.round((branchCost / units) * 100) / 100 : Math.round((globalCost / units) * 100) / 100;
    setRepackForm({ name: `R ${p.name}`, unit: 'Sachet', units_per_parent: units, cost_price: autoCost, add_on_cost: 0, prices: {}, barcode: '', branch_id: currentBranch.id });
    setRepackDialog(true);
  };

  // Coerce raw-string form fields → numbers right before sending to the
  // backend. We keep the in-progress typed value as a string in `form`
  // (so the user can type "1750." without it getting truncated mid-edit)
  // and normalise here at the boundary.
  const _normalizeMoney = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  const _buildSubmitPayload = (raw) => {
    const cleanPrices = {};
    for (const [k, v] of Object.entries(raw.prices || {})) {
      cleanPrices[k] = _normalizeMoney(v);
    }
    return {
      ...raw,
      cost_price: _normalizeMoney(raw.cost_price),
      prices: cleanPrices,
      ...(raw.starting_inventory !== undefined
        ? { starting_inventory: _normalizeMoney(raw.starting_inventory) }
        : {}),
    };
  };

  const handleSave = async () => {
    try {
      if (editing) {
        const submitForm = _buildSubmitPayload(form);
        // Strip cost_price from payload if user cannot edit capital
        const payload = canEditCost ? submitForm : (({ cost_price, ...rest }) => rest)(submitForm);
        // When a specific branch is selected, route price/cost edits to the
        // per-branch override collection by passing `branch_id` as a query
        // param. Catalog fields (name/category/etc.) still hit the master
        // server-side. With branchId="all" or no branch, behaviour matches
        // legacy: master product is updated directly.
        const branchId = currentBranch?.id;
        const isBranchScoped = branchId && branchId !== 'all';
        const cfg = isBranchScoped ? { params: { branch_id: branchId } } : undefined;
        await api.put(`/products/${editing.id}`, payload, cfg);
        toast.success(isBranchScoped
          ? `Saved to ${currentBranch.name} only — master catalog untouched`
          : 'Product updated (master catalog)');
      } else {
        const submitForm = _buildSubmitPayload(form);
        const res = await api.post('/products', submitForm);
        // Set starting inventory if provided
        const startingQty = _normalizeMoney(form.starting_inventory);
        if (startingQty > 0 && currentBranch) {
          await api.post('/inventory/set', {
            product_id: res.data.id, branch_id: currentBranch.id, quantity: startingQty
          });
        }
        toast.success('Product created');
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving product'); }
  };

  const handleRepack = async () => {
    if (!currentBranch?.id) { toast.error('Please select a branch first'); return; }
    try {
      // Normalize any raw-string price fields → numbers before POST.
      // (`updateRepackPrice` keeps the user's typed value as a string so
      // decimals don't get stripped while typing.)
      await api.post(
        `/products/${selectedParent.id}/generate-repack`,
        { ..._buildSubmitPayload(repackForm), branch_id: currentBranch.id }
      );
      toast.success('Repack SKU generated!');
      setRepackDialog(false);
      fetchProducts();
      refreshRepackIndicators();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error generating repack'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?\n\nThis is only allowed for products with ZERO history (no sales, POs, or stock movements). Products with history must be disabled at branch instead.')) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to delete';
      if (detail.includes('Cannot delete') || detail.includes('history')) {
        toast.error(detail, { duration: 8000 });
      } else {
        toast.error(detail);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} product(s)?\n\nOnly products with ZERO history will be deleted. Products with sales/PO history will be skipped and should be disabled at branch instead.`)) return;
    let deleted = 0;
    let blocked = 0;
    for (const id of selected) {
      try { await api.delete(`/products/${id}`); deleted++; }
      catch (e) {
        const detail = e.response?.data?.detail || '';
        if (detail.includes('Cannot delete') || detail.includes('history')) blocked++;
      }
    }
    if (deleted > 0) toast.success(`${deleted} product(s) deleted`);
    if (blocked > 0) toast.warning(`${blocked} product(s) skipped — they have sales/PO history. Use "Disable at Branch" for those.`, { duration: 8000 });
    setSelected(new Set());
    fetchProducts();
  };

  // ── Per-branch disable/enable ─────────────────────────────────────────────
  // A product can be disabled at a specific branch so it won't show up for
  // sale there. When stock returns (PO/transfer/return/manual), the product
  // is automatically re-enabled. Cannot disable a product with stock > 0.
  const handleBulkDisableAtBranch = async () => {
    if (!selected.size) return;
    if (!currentBranch?.id || currentBranch.id === 'all') {
      toast.error('Select a specific branch to disable products there');
      return;
    }
    if (!window.confirm(`Disable ${selected.size} product(s) at ${currentBranch.name}?\n\nProducts with stock will be skipped. They will auto-reactivate when stock arrives.`)) return;
    try {
      const res = await api.post('/products/disable-at-branch', {
        product_ids: Array.from(selected),
        branch_id: currentBranch.id,
      });
      const d = res.data || {};
      if (d.disabled_count > 0) toast.success(`Disabled ${d.disabled_count} product(s) at ${currentBranch.name}`);
      if (d.skipped_with_stock_count > 0) {
        const names = (d.skipped_with_stock || []).slice(0, 3).map(s => s.product_name).join(', ');
        const more = d.skipped_with_stock_count > 3 ? ` + ${d.skipped_with_stock_count - 3} more` : '';
        toast.warning(`Skipped ${d.skipped_with_stock_count} with stock: ${names}${more}`, { duration: 6000 });
      }
      setSelected(new Set());
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to disable');
    }
  };

  const handleBulkEnableAtBranch = async () => {
    if (!selected.size) return;
    if (!currentBranch?.id || currentBranch.id === 'all') {
      toast.error('Select a specific branch first');
      return;
    }
    try {
      const res = await api.post('/products/enable-at-branch', {
        product_ids: Array.from(selected),
        branch_id: currentBranch.id,
      });
      toast.success(`Enabled ${res.data?.enabled_count || 0} product(s) at ${currentBranch.name}`);
      setSelected(new Set());
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to enable');
    }
  };

  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner' || user?.is_super_admin;
  const canToggleBranch = !!currentBranch?.id && currentBranch.id !== 'all'
    && (isAdminOrOwner || user?.role === 'manager');

  const toggleSelect = (id) => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAll = () => {
    if (selected.size === displayedProducts.length) { setSelected(new Set()); }
    else { setSelected(new Set(displayedProducts.map(p => p.id))); }
  };

  // Keep the raw typed string in state — converting to float on every
  // keystroke (`parseFloat(v) || 0`) silently strips the trailing decimal
  // point, so a user could never type `1750.5`. We coerce to number
  // only at save-time inside `handleSave`.
  const updatePrice = (key, val) => setForm({ ...form, prices: { ...form.prices, [key]: val } });
  const updateRepackPrice = (key, val) => setRepackForm({ ...repackForm, prices: { ...repackForm.prices, [key]: val } });

  // Derived pagination values
  const displayedProducts = filteredProducts.slice(page * LIMIT, (page + 1) * LIMIT);
  const totalFiltered = filteredProducts.length;
  const totalPages = Math.ceil(totalFiltered / LIMIT);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="products-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-bold tracking-tight" style={{ fontFamily: 'Manrope', color: 'var(--text-primary)' }}>Products</h1>
          <p className="text-[13px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
            {debouncedSearch
              ? `${totalFiltered} of ${allProducts.length} products`
              : `${allProducts.length} products`
            } &middot; Parent &amp; Repack management
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdminOrOwner && (
            <Button variant="outline" size="sm" data-testid="open-vocab-cleanup-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('product-vocabulary-open'))}
              title="Find the same unit or category spelled more than one way">
              <Tag size={15} className="mr-1.5 text-amber-600" /> Clean up units
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate('/import')} data-testid="go-to-import-btn">
            <Upload size={15} className="mr-1.5" /> Import
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={exporting} data-testid="export-products-btn">
            <Download size={15} className="mr-1.5 text-slate-600" /> {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleBulkGenerateBarcodes} disabled={barcodeGenerating} data-testid="bulk-barcode-btn">
            <ScanBarcode size={15} className="mr-1.5 text-blue-500" /> {barcodeGenerating ? 'Generating...' : 'Generate Barcodes'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/barcode-print')} data-testid="print-barcodes-nav-btn">
            <ScanBarcode size={15} className="mr-1.5 text-emerald-600" /> Print Barcodes
          </Button>
          {canCreateProduct && (
            <Button variant="outline" size="sm" onClick={openQrModal} data-testid="quick-repack-btn">
              <Zap size={15} className="mr-1.5 text-amber-500" /> Quick Repack
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate('/products/repack-pricing')} data-testid="repack-pricing-nav-btn">
            <Tag size={15} className="mr-1.5 text-amber-600" /> Repack Pricing
          </Button>
          <Button variant="outline" size="sm" onClick={() => { loadCatMgmt(); setCatMgmtOpen(true); }} data-testid="manage-categories-btn">
            <Tag size={15} className="mr-1.5 text-violet-500" /> Categories
          </Button>
          {canCreateProduct && (
            <Button data-testid="create-product-btn" onClick={() => openCreate()} className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">
              <Plus size={16} className="mr-2" /> Add Product
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="product-search"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search name, SKU, barcode…"
            className="pl-9 pr-8 h-10"
          />
          {search && (
            <button onClick={() => { setSearch(''); setDebouncedSearch(''); setFuzzyHint(null); setPage(0); }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              data-testid="clear-search-btn" title="Clear search">
              <X size={14} />
            </button>
          )}
        </div>
        <Select value={filter} onValueChange={v => { setFilter(v); setPage(0); }}>
          <SelectTrigger data-testid="product-filter" className="w-[160px] h-10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Products</SelectItem>
            <SelectItem value="parent">Parent Only</SelectItem>
            <SelectItem value="repack">Repacks Only</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={v => { setSortBy(v); setPage(0); }}>
          <SelectTrigger data-testid="product-sort" className="w-[185px] h-10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name">Sort: Name (A–Z)</SelectItem>
            <SelectItem value="type">Sort: Type (Parents first)</SelectItem>
            <SelectItem value="grouped">Sort: Grouped (Repacks below parent)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 animate-fadeIn">
          <span className="text-sm font-medium text-slate-700">{selected.size} selected</span>
          {canToggleBranch && (
            <>
              <Button size="sm" variant="outline" onClick={handleBulkDisableAtBranch}
                data-testid="bulk-disable-branch-btn"
                className="border-amber-300 text-amber-700 hover:bg-amber-50">
                <EyeOff size={13} className="mr-1" /> Disable at {currentBranch.name}
              </Button>
              <Button size="sm" variant="outline" onClick={handleBulkEnableAtBranch}
                data-testid="bulk-enable-branch-btn"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                <Eye size={13} className="mr-1" /> Enable at {currentBranch.name}
              </Button>
            </>
          )}
          {isAdminOrOwner && (
            <Button size="sm" variant="outline"
              onClick={handleBulkDelete}
              data-testid="bulk-delete-btn"
              className="border-red-300 text-red-600 hover:bg-red-50"
              title="Delete products with zero history only. Products with sales/PO history will be skipped.">
              <Trash2 size={13} className="mr-1" /> Delete (zero history only)
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="text-slate-500 text-xs">Clear</Button>
        </div>
      )}

      {/* Fuzzy match banner */}
      {fuzzyHint && (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="fuzzy-hint-banner">
          <AlertTriangle size={13} className="shrink-0" />
          <span>Showing fuzzy matches for <b>"{fuzzyHint.query}"</b> — {fuzzyHint.count} result{fuzzyHint.count !== 1 ? 's' : ''}. Typo? Try a more exact spelling.</span>
          <button onClick={() => { setSearch(''); setDebouncedSearch(''); setFuzzyHint(null); }} className="ml-auto text-amber-600 hover:text-amber-800 font-medium underline shrink-0">Clear</button>
        </div>
      )}

      {/* Products Table */}
      <Card style={{ border: '1px solid var(--border-default)' }}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow style={{ backgroundColor: '#F1F0ED' }}>
                <TableHead className="w-10"><input type="checkbox" checked={displayedProducts.length > 0 && selected.size === displayedProducts.length} onChange={toggleAll} className="rounded border-slate-300 cursor-pointer" /></TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium">SKU</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium">Product Name</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium">Category</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium">Unit</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium text-right" title={currentBranch?.id && currentBranch.id !== 'all' ? `On-hand at ${currentBranch.name}` : 'Total on-hand across all branches'}>
                  Stock
                </TableHead>
                {canViewCost && <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium text-right">Cost</TableHead>}
                {schemes.map(s => (
                  <TableHead key={s.key} className="text-xs uppercase tracking-wider text-slate-500 font-medium text-right capitalize">{s.name}</TableHead>
                ))}
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium">Barcode</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium">Type</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium w-32">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingProducts && allProducts.length === 0 ? (
                <TableRow><TableCell colSpan={9 + schemes.length} className="text-center py-10 text-slate-400">
                  <RefreshCw size={18} className="animate-spin inline mr-2 text-slate-300" />Loading products…
                </TableCell></TableRow>
              ) : displayedProducts.length === 0 && debouncedSearch ? (
                <TableRow><TableCell colSpan={9 + schemes.length} className="text-center py-10 text-slate-400">
                  No products match <b>"{debouncedSearch}"</b> — try a different spelling
                </TableCell></TableRow>
              ) : displayedProducts.length === 0 ? (
                <TableRow><TableCell colSpan={9 + schemes.length} className="text-center py-8 text-slate-400">No products found</TableCell></TableRow>
              ) : null}
              {displayedProducts.map(p => (
                <TableRow key={p.id} className={`table-row-hover ${selected.has(p.id) ? 'bg-blue-50/50' : ''} ${p.disabled_at_branch ? 'opacity-60 bg-slate-50/40' : ''}`}>
                  <TableCell><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} className="rounded border-slate-300 cursor-pointer" /></TableCell>
                  <TableCell className="text-xs" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>{p.sku}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 flex-wrap">
                      {p.is_repack && <span className="w-4 border-l-2 border-b-2 border-slate-300 h-3 inline-block" />}
                      <span className="font-medium">{p.name}</span>
                      {p.disabled_at_branch && (
                        <Badge variant="outline" data-testid={`disabled-here-${p.id}`}
                          className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 gap-1 px-1.5 py-0">
                          <EyeOff size={9} /> Disabled here
                        </Badge>
                      )}
                      {/* Branch override chip — surfaces when this row's
                          prices come from a per-branch override instead of
                          the master. Only shown when a specific branch is
                          selected; "All Branches" view tags everything as
                          global by definition. */}
                      {p.price_source === 'branch_override' && currentBranch?.id && currentBranch.id !== 'all' && (
                        <Badge variant="outline" data-testid={`branch-override-${p.id}`}
                          className="text-[10px] border-purple-300 text-purple-700 bg-purple-50 gap-1 px-1.5 py-0"
                          title={`Custom price for ${currentBranch.name} — different from master catalog`}>
                          <Tag size={9} /> Branch
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-500">{p.category}</TableCell>
                  <TableCell>{p.unit}</TableCell>
                  <TableCell className="text-right text-sm" data-testid={`stock-${p.id}`} style={{ fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"' }}>
                    {(() => {
                      const s = Number(p.stock_on_hand ?? 0);
                      if (s <= 0) return <span className="text-red-500">0</span>;
                      return <span className="text-slate-700">{Number.isInteger(s) ? s : s.toFixed(2)}</span>;
                    })()}
                  </TableCell>
                  {canViewCost && <TableCell className="text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(p.cost_price)}</TableCell>}
                  {schemes.map(s => (
                    <TableCell key={s.key} className="text-right text-sm" style={{ fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"' }}>
                      {p.prices?.[s.key] > 0 ? formatPHP(p.prices[s.key]) : <span className="text-slate-300 text-xs">—</span>}
                    </TableCell>
                  ))}
                  <TableCell>
                    {p.barcode ? (
                      <code className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">{p.barcode}</code>
                    ) : (
                      <span className="text-[10px] text-slate-300">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.is_repack ? (
                      <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">Repack</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50">Parent</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" data-testid={`view-product-${p.id}`} onClick={() => navigate(`/products/${p.id}`)} title="View Details">
                        <Eye size={14} className="text-blue-500" />
                      </Button>
                      {!p.is_repack && canCreateProduct && (
                        <Button variant="ghost" size="sm"
                          data-testid={`repack-btn-${p.id}`}
                          onClick={() => openRepack(p)}
                          title={repackParentIds.has(p.id) ? `Has repack — click to add another` : 'No repack yet — click to generate'}
                          className={repackParentIds.has(p.id) ? 'text-emerald-600 hover:text-emerald-700' : 'text-red-400 hover:text-red-500'}
                        >
                          <Link2 size={14} />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm"
                        data-testid={`edit-product-${p.id}`}
                        onClick={() => openEdit(p)}
                        title={canEditProduct ? 'Edit product' : 'View product (read-only — no edit permission)'}
                      >
                        {canEditProduct
                          ? <Pencil size={14} />
                          : <Eye size={14} className="text-slate-400" />}
                      </Button>
                      {isAdminOrOwner && !p.is_repack && (
                        <Button variant="ghost" size="sm"
                          data-testid={`inject-stock-${p.id}`}
                          onClick={() => { setInjectionProduct(p); setInjectionOpen(true); }}
                          className="text-red-500 hover:text-red-700"
                          title="Admin Stock Injection (bypasses PO / Transfer; audit logged)">
                          <ShieldAlert size={14} />
                        </Button>
                      )}
                      {isAdminOrOwner && (
                        <Button variant="ghost" size="sm"
                          data-testid={`delete-product-${p.id}`}
                          onClick={() => handleDelete(p.id)}
                          className="text-red-400 hover:text-red-600"
                          title="Delete product (blocked if product has any sales/PO/stock history — use Disable at Branch instead)">
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination + live refresh button */}
      <div className="flex items-center justify-between">
        {totalPages > 1 ? (
          <>
            <p className="text-xs text-slate-500">
              Showing {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, totalFiltered)} of {totalFiltered}
              {debouncedSearch ? ` results` : ` products`}
            </p>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>Next</Button>
            </div>
          </>
        ) : (
          <p className="text-xs text-slate-400">
            {loadingProducts
              ? 'Refreshing live data…'
              : `${totalFiltered} product${totalFiltered !== 1 ? 's' : ''}${debouncedSearch ? ' found' : ' loaded'}`}
          </p>
        )}
        <Button variant="ghost" size="sm" className="text-xs text-slate-400 hover:text-slate-600 gap-1"
          onClick={() => fetchProducts()} disabled={loadingProducts}
          data-testid="refresh-products-btn" title="Refresh live data">
          <RefreshCw size={12} className={loadingProducts ? 'animate-spin' : ''} />
          {loadingProducts ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      {/* Product Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {editing ? (canEditProduct ? 'Edit Product' : 'View Product') : 'New Product'}
            </DialogTitle>
          </DialogHeader>
          {editing && !canEditProduct && (
            <div data-testid="product-readonly-banner"
              className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2">
              <Lock size={12} /> Read-only — you do not have permission to edit products.
            </div>
          )}
          {/* ── Edit-scope banner ─────────────────────────────────────────
              Surfaces WHERE the edit will land. Without this, branch users
              were silently editing the master catalog (a footgun that
              clobbered every other branch's price). Now the cashier sees
              "Editing for {Branch} only" before they even type. */}
          {editing && (
            currentBranch?.id && currentBranch.id !== 'all' ? (
              <div
                data-testid="edit-scope-banner-branch"
                className="mt-2 rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-xs text-purple-900 leading-relaxed">
                <p className="font-semibold flex items-center gap-1">
                  <Tag size={12} /> Editing prices for <strong>{currentBranch.name}</strong> only
                </p>
                <p className="mt-0.5 text-purple-700">
                  Master catalog stays untouched. Other branches keep their existing prices.
                  Switch to <em>All Branches</em> at the top to edit the master.
                </p>
              </div>
            ) : (
              <div
                data-testid="edit-scope-banner-global"
                className="mt-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 leading-relaxed">
                <p className="font-semibold flex items-center gap-1">
                  <Package size={12} /> Editing master catalog (all branches)
                </p>
                <p className="mt-0.5 text-blue-700">
                  These prices apply to every branch unless that branch already has its own override.
                  To edit one branch only, pick it in the branch switcher first.
                </p>
              </div>
            )
          )}
          <div className="space-y-4 mt-2">
            <fieldset disabled={!!editing && !canEditProduct} className="space-y-4 disabled:opacity-80">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>SKU <span className="text-xs text-muted-foreground font-normal">(optional - auto-generated if blank)</span></Label>
                <Input data-testid="product-sku-input" value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} placeholder="Leave blank to auto-generate" disabled={!!editing} />
              </div>
              <div>
                <Label>Product Name</Label>
                <Input data-testid="product-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Lannate 250g" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Category</Label>
                <CategorySelect
                  value={form.category}
                  onChange={v => setForm({ ...form, category: v })}
                  testId="product-category-input"
                />
              </div>
              <div>
                <Label>Unit</Label>
                <Input data-testid="product-unit-input" list="product-unit-options" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} placeholder="Box, Bag, Bottle, Pack" />
                <datalist id="product-unit-options">
                  {unitOptions.map(u => <option key={u} value={u} />)}
                </datalist>
                {unitTwin(form.unit) && (
                  <p className="text-[11px] text-amber-700 mt-1" data-testid="product-unit-twin">
                    Your catalog already uses <b>{unitTwin(form.unit)}</b> — this will be saved as that.{' '}
                    <button type="button" className="underline font-medium"
                      onClick={() => setForm({ ...form, unit: unitTwin(form.unit) })}>Use {unitTwin(form.unit)}</button>
                  </p>
                )}
              </div>
              {canViewCost && (
              <div>
                <Label className="flex items-center gap-1.5">
                  Cost Price / Capital
                  {editing && !canEditCost && (
                    <span className="text-[10px] text-amber-600 font-normal flex items-center gap-0.5">
                      <Lock size={10} /> locked
                    </span>
                  )}
                </Label>
                <CalcInput data-testid="product-cost-input"
 value={form.cost_price}
 onChange={(v) => setForm({ ...form, cost_price: v })}
 disabled={!!editing && !canEditCost}
 title={editing && !canEditCost ? 'You do not have permission to edit the capital/cost price' : ''}
 className={editing && !canEditCost ? 'bg-slate-100 cursor-not-allowed opacity-60' : ''} />
                {editing && !canEditCost && (
                  <p className="text-[10px] text-amber-600 mt-0.5">No permission to edit capital</p>
                )}
              </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={form.product_type || 'stockable'} onValueChange={v => setForm({ ...form, product_type: v })}>
                  <SelectTrigger data-testid="product-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stockable">Stockable</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Barcode</Label>
                <Input value={form.barcode} onChange={e => setForm({ ...form, barcode: e.target.value })} placeholder="Optional" />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional" />
              </div>
              <div>
                <Label className="flex items-center gap-1.5">
                  VAT Classification
                  <span className="text-[10px] text-slate-400 font-normal">(for BIR)</span>
                </Label>
                <Select value={form.vat_class || 'vatable'} onValueChange={v => setForm({ ...form, vat_class: v })}>
                  <SelectTrigger data-testid="product-vat-class-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vatable">Vatable (12% VAT)</SelectItem>
                    <SelectItem value="vat_exempt">VAT-Exempt</SelectItem>
                    <SelectItem value="zero_rated">Zero-Rated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold">Price Schemes</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {schemes.map(s => (
                  <div key={s.id}>
                    <Label className="text-xs text-slate-500">{s.name}</Label>
                    <CalcInput data-testid={`price-${s.key}`}
 value={form.prices[s.key] || ''}
 onChange={(v) => updatePrice(s.key, v)}
 placeholder="0.00" />
                  </div>
                ))}
              </div>
            </div>
            {!editing && (
              <div>
                <Label>Starting Inventory ({currentBranch?.name || 'Current Branch'})</Label>
                <CalcInput data-testid="product-starting-inventory" value={form.starting_inventory || 0} onChange={(v) => setForm({ ...form, starting_inventory: v })} placeholder="0" />
                <p className="text-[11px] text-slate-400 mt-0.5">Leave 0 if no stock yet</p>
              </div>
            )}
            </fieldset>

            {/* ── Inventory Correction (edit mode only, requires inventory.adjust) ── */}
            {editing && !editing.is_repack && canAdjustInventory && (
              <div className="border border-dashed border-slate-300 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setCorrectionExpanded(v => !v)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-sm font-medium text-slate-700"
                  data-testid="correction-toggle-btn"
                >
                  <span className="flex items-center gap-2">
                    <History size={15} className="text-blue-500" />
                    Inventory Correction
                    {currentStock !== null && (
                      <Badge variant="outline" className="text-[10px] border-blue-200 text-blue-700 bg-blue-50">
                        Current: {currentStock}
                      </Badge>
                    )}
                  </span>
                  <span className="text-slate-400 text-xs">{correctionExpanded ? '▲ Hide' : '▼ Show'}</span>
                </button>

                {correctionExpanded && (
                  <div className="p-4 space-y-3">
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2">
                      <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                      <span>
                        {!currentBranch
                          ? 'Please select a specific branch first (not "All Branches") to correct inventory.'
                          : <>This directly sets the stock count for <strong>{currentBranch?.name}</strong>.
                            Use only to correct counting errors. Every change is logged.
                            {user?.role !== 'admin' && ' Requires TOTP verification.'}</>
                        }
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">New Quantity</Label>
                        <CalcInput data-testid="correction-qty-input"
 value={correctionQty}
 onChange={(v) => setCorrectionQty(v)}
 placeholder={currentStock !== null ? `Current: ${currentStock}` : 'Enter qty'}
 className="h-9 mt-1"
 disabled={!currentBranch} />
                      </div>
                      <div>
                        <Label className="text-xs">Reason <span className="text-red-400">*</span></Label>
                        <Input
                          data-testid="correction-reason-input"
                          value={correctionReason}
                          onChange={e => setCorrectionReason(e.target.value)}
                          placeholder="e.g. Physical count correction"
                          className="h-9 mt-1"
                          disabled={!currentBranch}
                        />
                      </div>
                    </div>

                    <Button
                      data-testid="apply-correction-btn"
                      size="sm"
                      onClick={handleCorrectionSubmit}
                      disabled={correctionSaving || !correctionQty || !correctionReason.trim() || !currentBranch}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {correctionSaving
                        ? <><RefreshCw size={13} className="animate-spin mr-1.5" /> Applying...</>
                        : <><History size={13} className="mr-1.5" /> Apply Correction</>}
                    </Button>

                    {/* Recent corrections log */}
                    {correctionHistory.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-semibold text-slate-500 mb-1.5">Recent Corrections</p>
                        <div className="rounded-lg border border-slate-200 overflow-hidden text-xs">
                          {correctionHistory.slice(0, 5).map((c, i) => (
                            <div key={c.id} className={`flex items-center justify-between px-3 py-2 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} border-b last:border-0`}>
                              <div className="flex items-center gap-2 min-w-0">
                                <span className={` font-bold ${c.difference > 0 ? 'text-emerald-600' : c.difference < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                                  {c.old_quantity} → {c.new_quantity}
                                </span>
                                <span className="text-slate-500 truncate">{c.reason}</span>
                              </div>
                              <div className="flex items-center gap-2 text-right text-slate-400 shrink-0 ml-2">
                                <div>
                                  <p>{c.performed_by_name}</p>
                                  <p>{c.auth_mode === 'totp' ? '🔐 TOTP' : c.auth_mode === 'password' ? '🔑 PW' : '👤 Admin'}</p>
                                </div>
                                {c.id && (
                                  <button onClick={() => { setCorrUploadId(c.id); setCorrUploadQROpen(true); }}
                                    className="w-6 h-6 rounded bg-blue-50 hover:bg-blue-100 flex items-center justify-center" title="Upload damage photo">
                                    <Upload size={10} className="text-blue-600" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                {editing && !canEditProduct ? 'Close' : 'Cancel'}
              </Button>
              {((editing && canEditProduct) || (!editing && canCreateProduct)) && (
                <Button data-testid="save-product-btn" onClick={handleSave} className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">Save Product</Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Repack Already Exists — Confirmation Dialog */}
      <Dialog open={!!repackConfirmParent} onOpenChange={() => setRepackConfirmParent(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
              <Link2 size={18} className="text-emerald-600" /> Repack Already Exists
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm">
              <p className="font-semibold text-emerald-800">{repackConfirmParent?.name}</p>
              <p className="text-emerald-600 text-xs mt-0.5">
                This product already has at least one repack SKU. You can generate an additional repack
                if needed (e.g., a different unit size).
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Button
                className="bg-[#1A4D2E] hover:bg-[#14532d] text-white"
                onClick={() => { _launchRepackDialog(repackConfirmParent); setRepackConfirmParent(null); }}
              >
                <Link2 size={14} className="mr-2" /> Generate Another Repack
              </Button>
              <Button variant="outline"
                onClick={() => { navigate(`/products/${repackConfirmParent?.id}`); setRepackConfirmParent(null); }}
              >
                View Existing Repacks
              </Button>
              <Button variant="ghost" onClick={() => setRepackConfirmParent(null)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Repack Dialog */}
      <Dialog open={repackDialog} onOpenChange={setRepackDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Generate Repack SKU</DialogTitle>
          </DialogHeader>
          {selectedParent && (
            <div className="space-y-4 mt-2">
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-sm">
                <p className="font-semibold text-emerald-800">Parent: {selectedParent.name}</p>
                <p className="text-emerald-600 text-xs">SKU: {selectedParent.sku} &middot; Unit: {selectedParent.unit}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Repack Name</Label>
                  <Input data-testid="repack-name-input" value={repackForm.name} onChange={e => setRepackForm({ ...repackForm, name: e.target.value })} />
                </div>
                <div>
                  <Label>Unit</Label>
                  <Input data-testid="repack-unit-input" value={repackForm.unit} onChange={e => setRepackForm({ ...repackForm, unit: e.target.value })} placeholder="Sachet, Pack, Piece" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Units per Parent ({selectedParent.unit})</Label>
                  <CalcInput data-testid="repack-units-per-parent"
 value={repackForm.units_per_parent}
 onChange={(v) => {
 const units = parseInt(v) || 1;
 const autoCost = selectedParent.cost_price ? Math.round((selectedParent.cost_price / units + (repackForm.add_on_cost || 0)) * 100) / 100 : 0;
 setRepackForm({ ...repackForm, units_per_parent: units, cost_price: autoCost });
 }} />
                  <p className="text-[11px] text-slate-500 mt-1">How many {repackForm.unit || 'pieces'} inside 1 {selectedParent.unit}?</p>
                </div>
                <div>
                  <Label>Add-on Cost (optional)</Label>
                  <CalcInput data-testid="repack-addon-cost" value={repackForm.add_on_cost || 0}
 onChange={(v) => {
 const addon = parseFloat(v) || 0;
 const units = repackForm.units_per_parent || 1;
 const autoCost = Math.round((selectedParent.cost_price / units + addon) * 100) / 100;
 setRepackForm({ ...repackForm, add_on_cost: addon, cost_price: autoCost });
 }}
 placeholder="Extra cost per repack" />
                  <p className="text-[11px] text-slate-500 mt-1">Repacking labor, packaging, etc.</p>
                </div>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm">
                <p className="text-blue-800 font-medium">Auto-computed Cost per {repackForm.unit || 'unit'}:</p>
                <p className="text-blue-900 text-lg font-bold" style={{ fontFamily: 'Manrope' }}>
                  ₱{(selectedParent.cost_price / (repackForm.units_per_parent || 1)).toFixed(2)} (parent ÷ {repackForm.units_per_parent})
                  {(repackForm.add_on_cost || 0) > 0 && <span> + ₱{repackForm.add_on_cost.toFixed(2)} add-on</span>}
                  {' '}= <span className="text-emerald-700">₱{repackForm.cost_price.toFixed(2)}</span>
                </p>
              </div>
              {/* Barcode — optional, can come from Generate Repack SKU on this page */}
              <div>
                <Label>Repack Barcode <span className="text-xs font-normal text-slate-400">(optional — scan or paste from "Generate Repack SKU" above)</span></Label>
                <Input
                  data-testid="repack-barcode-input"
                  value={repackForm.barcode || ''}
                  onChange={e => setRepackForm({ ...repackForm, barcode: e.target.value })}
                  placeholder="e.g. AG53868703 — leave blank to auto-skip"
                  className="font-mono"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold">Repack Prices</Label>                <div className="grid grid-cols-2 gap-3 mt-2">
                  {schemes.map(s => (
                    <div key={s.id}>
                      <Label className="text-xs text-slate-500">{s.name}</Label>
                      <CalcInput value={repackForm.prices[s.key] || ''} onChange={(v) => updateRepackPrice(s.key, v)} placeholder="0.00" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setRepackDialog(false)}>Cancel</Button>
                <Button data-testid="generate-repack-btn" onClick={handleRepack} className="bg-[#D97706] hover:bg-[#b45309] text-white">
                  <Link2 size={16} className="mr-2" /> Generate Repack
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* ── Batch Quick Repack Generator ─────────────────────────────── */}      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-[96vw] w-[1100px] max-h-[90vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
              <Zap size={18} className="text-amber-500" /> Quick Repack Generator
              <span className="text-sm font-normal text-slate-500 ml-1">— fill each row, then Generate All</span>
            </DialogTitle>
          </DialogHeader>

          {/* ── Results view (after generation) ── */}
          {qrResults ? (
            <div className="flex-1 overflow-auto space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-center">
                  <div className="text-2xl font-bold text-emerald-700">{qrResults.filter(r => r.status === 'ok').length}</div>
                  <div className="text-xs text-emerald-600">Repacks Created</div>
                </div>
                <div className={`p-3 rounded-lg border text-center ${qrResults.filter(r => r.status === 'error').length > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
                  <div className={`text-2xl font-bold ${qrResults.filter(r => r.status === 'error').length > 0 ? 'text-red-700' : 'text-slate-400'}`}>{qrResults.filter(r => r.status === 'error').length}</div>
                  <div className="text-xs text-slate-500">Failed</div>
                </div>
              </div>
              <div className="border rounded-lg overflow-hidden">
                {qrResults.map((r, i) => (
                  <div key={i} className={`flex items-center gap-3 px-4 py-2.5 text-sm border-b last:border-0 ${r.status === 'ok' ? 'bg-emerald-50/50' : 'bg-red-50/50'}`}>
                    {r.status === 'ok' ? <CheckCircle size={15} className="text-emerald-600 shrink-0" /> : <XCircle size={15} className="text-red-500 shrink-0" />}
                    <span className="font-medium flex-1">{r.name}</span>
                    {r.status === 'ok' ? <span className="text-xs text-emerald-600">Created</span> : <span className="text-xs text-red-500">{r.error}</span>}
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => { setQrRows([newRow()]); setQrResults(null); }}>
                  <Zap size={14} className="mr-1.5 text-amber-500" /> Generate More
                </Button>
                <Button onClick={() => setQrOpen(false)} className="bg-[#1A4D2E] text-white">Done</Button>
              </div>
            </div>
          ) : (
            /* ── Spreadsheet table ── */
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="overflow-x-auto flex-1 overflow-y-visible"
                   style={{ overflowY: 'visible' }}>
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b-2 border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-2 py-2 text-left font-semibold" style={{minWidth:'220px'}}>Parent Product <span className="text-red-400">*</span></th>
                      <th className="px-2 py-2 text-left font-semibold" style={{minWidth:'180px'}}>Repack Name <span className="text-red-400">*</span></th>
                      <th className="px-2 py-2 text-left font-semibold" style={{minWidth:'90px'}}>Unit</th>
                      <th className="px-2 py-2 text-center font-semibold" style={{minWidth:'90px'}}>Qty / Box</th>
                      <th className="px-2 py-2 text-center font-semibold" style={{minWidth:'90px'}}>Add-on</th>
                      <th className="px-2 py-2 text-center font-semibold" style={{minWidth:'100px'}}>Capital <span className="text-[10px] normal-case tracking-normal font-normal text-slate-400">(auto)</span></th>
                      <th className="px-2 py-2 text-center font-semibold" style={{minWidth:'115px'}}>Retail Price <span className="text-red-400">*</span></th>
                      <th className="px-2 py-2 text-left font-semibold" style={{minWidth:'130px'}}>Barcode <span className="text-[10px] normal-case tracking-normal font-normal text-slate-400">(optional)</span></th>
                      <th className="px-2 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {qrRows.map((row) => {
                      const hasErr = !!row.rowError;
                      // Live-derived duplicate flag: true if any OTHER row has the
                      // same parent id. Recomputes automatically when rows change.
                      const dupInSession = !!(row.parent?.id && qrRows.some(r => r.id !== row.id && r.parent?.id === row.parent.id));
                      return (
                        <tr key={row.id} className={`border-b border-slate-100 transition-colors ${hasErr ? 'bg-red-50' : 'hover:bg-slate-50/50'}`}>

                          {/* Parent search / selected */}
                          <td className="px-2 py-1.5" style={{minWidth:'220px'}}>
                            {row.parent ? (
                              <div>
                                <div className={`flex items-center gap-1.5 h-8 px-2 rounded border ${
                                  hasErr && !row.parent ? 'border-red-400'
                                  : dupInSession ? 'border-orange-400 bg-orange-50'
                                  : 'border-emerald-300 bg-emerald-50'
                                }`}>
                                  <span className={`text-sm truncate flex-1 font-medium ${dupInSession ? 'text-orange-800' : 'text-emerald-800'}`}>{row.parent.name}</span>
                                  <button onClick={() => updateRow(row.id, { parent: null, parentSearch: '', capital: 0, repackName: '', retailPrice: '', retailError: null, hasRepack: false, dupInSession: false })}
                                    className="text-slate-400 hover:text-red-500 shrink-0">
                                    <X size={12} />
                                  </button>
                                </div>
                                {dupInSession && (
                                  <p className="text-[10px] text-orange-600 mt-0.5 flex items-center gap-1 leading-none font-semibold">
                                    <AlertTriangle size={10} /> Duplicate parent in this batch
                                  </p>
                                )}
                                {!dupInSession && row.hasRepack && (
                                  <p className="text-[10px] text-amber-600 mt-0.5 flex items-center gap-1 leading-none">
                                    <AlertTriangle size={10} /> Already has a repack
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div className="relative">
                                <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                                <Input value={row.parentSearch} placeholder="Search parent..."
                                  onChange={e => searchParent(row.id, e.target.value)}
                                  onKeyDown={e => handleParentKeyDown(e, row)}
                                  onBlur={() => setTimeout(() => updateRow(row.id, { parentMatches: [], activeSearchIndex: -1 }), 150)}
                                  className={`h-8 pl-7 text-sm ${hasErr ? 'border-red-400 bg-red-50/50' : ''}`}
                                  data-testid={`qr-parent-${row.id}`}
                                  ref={el => { dropdownRefs.current[row.id] = el; }}
                                  autoComplete="one-time-code" data-lpignore="true"
                                />
                                {row.parentMatches.length > 0 && (() => {
                                  const inputEl = dropdownRefs.current[row.id];
                                  if (!inputEl) return null;
                                  const rect = inputEl.getBoundingClientRect();
                                  // Compute set of parent ids already used in OTHER rows
                                  const usedParentIds = new Set(
                                    qrRows.filter(r => r.id !== row.id && r.parent?.id).map(r => r.parent.id)
                                  );
                                  return createPortal(
                                    <div
                                      style={{
                                        position: 'fixed',
                                        top: rect.bottom + 4,
                                        left: rect.left,
                                        width: Math.max(rect.width, 256),
                                        zIndex: 9999,
                                      }}
                                      className="bg-white border border-slate-200 rounded-lg shadow-xl max-h-44 overflow-y-auto"
                                    >
                                      {row.parentMatches.map((p, idx) => {
                                        const inBatch = usedParentIds.has(p.id);
                                        return (
                                          <button
                                            key={p.id}
                                            data-qr-option={`${row.id}-${idx}`}
                                            onMouseDown={() => selectParent(row.id, p)}
                                            className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 transition-colors ${
                                              idx === row.activeSearchIndex
                                                ? 'bg-emerald-50 border-l-[3px] border-l-emerald-700'
                                                : 'hover:bg-slate-50'
                                            }`}>
                                            <div className="font-medium text-sm truncate flex items-center gap-1.5">
                                              {p.name}
                                              {inBatch && (
                                                <span className="text-[10px] text-orange-600 font-semibold flex items-center gap-0.5 shrink-0 bg-orange-50 px-1 py-0.5 rounded">
                                                  <AlertTriangle size={9} /> in this batch
                                                </span>
                                              )}
                                              {!inBatch && repackParentIds.has(p.id) && (
                                                <span className="text-[10px] text-amber-600 font-normal flex items-center gap-0.5 shrink-0">
                                                  <AlertTriangle size={9} /> has repack
                                                </span>
                                              )}
                                            </div>
                                            <div className="text-xs text-slate-400">{p.unit} · Cost ₱{p.cost_price}</div>
                                          </button>
                                        );
                                      })}
                                      <div className="px-3 py-1 bg-slate-50 border-t text-[10px] text-slate-400 flex gap-3">
                                        <span>↑↓ navigate</span><span>Enter to select</span><span>Esc to close</span>
                                      </div>
                                    </div>,
                                    document.body
                                  );
                                })()}
                              </div>
                            )}
                            {hasErr && !row.parent && <p className="text-[10px] text-red-500 mt-0.5 leading-none">{row.rowError}</p>}
                          </td>

                          {/* Repack Name */}
                          <td className="px-2 py-1.5" style={{minWidth:'180px'}}>
                            <Input value={row.repackName} placeholder="R Product Name"
                              onChange={e => updateRow(row.id, { repackName: e.target.value })}
                              className={`h-8 text-sm ${hasErr && !row.repackName.trim() ? 'border-red-400 bg-red-50/50' : ''}`}
                              data-testid={`qr-name-${row.id}`} />
                          </td>

                          {/* Unit */}
                          <td className="px-2 py-1.5" style={{minWidth:'90px'}}>
                            <Input value={row.unit} placeholder="Pack"
                              onChange={e => updateRow(row.id, { unit: e.target.value })}
                              className="h-8 text-sm" data-testid={`qr-unit-${row.id}`} />
                          </td>

                          {/* Qty per parent */}
                          <td className="px-2 py-1.5" style={{minWidth:'90px'}}>
                            <CalcInput value={row.unitsPerParent}
 onChange={(raw) => {
 const v = parseInt(raw) || 1;
 updateRow(row.id, { unitsPerParent: v, capital: computeCapital(row.parent, v, row.addOnCost) });
 }}
 className="h-8 text-sm text-right"
 data-testid={`qr-units-${row.id}`} />
                          </td>

                          {/* Add-on cost */}
                          <td className="px-2 py-1.5" style={{minWidth:'90px'}}>
                            <CalcInput value={row.addOnCost}
 onChange={(raw) => {
 const v = parseFloat(raw) || 0;
 updateRow(row.id, { addOnCost: v, capital: computeCapital(row.parent, row.unitsPerParent, v) });
 }}
 className="h-8 text-sm text-right"
 data-testid={`qr-addon-${row.id}`} />
                          </td>

                          {/* Capital — read-only computed */}
                          <td className="px-2 py-1.5" style={{minWidth:'100px'}}>
                            <div className={`h-8 px-2 flex items-center justify-center text-sm  rounded border ${row.capital > 0 ? 'bg-blue-50 border-blue-200 text-blue-800 font-semibold' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>
                              {row.capital > 0 ? `₱${row.capital.toFixed(2)}` : '—'}
                            </div>
                          </td>

                          {/* Retail price — blank, validated on blur */}
                          <td className="px-2 py-1.5" style={{minWidth:'115px'}}>
                            <CalcInput value={row.retailPrice}
                              placeholder="0.00"
                              onChange={(v) => updateRow(row.id, { retailPrice: v, retailError: null })}
                              onBlur={e => handleRetailBlur(row.id, e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Tab' && !e.shiftKey) {
                                  e.preventDefault();
                                  // Only add a new row if ALL existing rows have a parent selected
                                  // If there's already an empty row, focus it instead
                                  const emptyRow = qrRows.find(r => !r.parent);
                                  if (emptyRow) {
                                    const input = document.querySelector(`[data-testid="qr-parent-${emptyRow.id}"]`);
                                    if (input) input.focus();
                                  } else {
                                    const next = newRow();
                                    pendingFocusRowId.current = next.id;
                                    setQrRows(rows => [...rows, next]);
                                  }
                                }
                              }}
                              className={`h-8 text-sm text-right  font-bold ${
                                (hasErr && !row.retailPrice) || row.retailError ? 'border-red-400 bg-red-50/50 text-red-700' : ''
                              }`}
                              data-testid={`qr-retail-${row.id}`} />
                            {row.retailError && (
                              <p className="text-[10px] text-red-500 mt-0.5 leading-none whitespace-nowrap">{row.retailError}</p>
                            )}
                            {hasErr && !row.retailPrice && !row.retailError && (
                              <p className="text-[10px] text-red-500 mt-0.5 leading-none">Required</p>
                            )}
                          </td>

                          {/* Barcode — optional */}
                          <td className="px-2 py-1.5" style={{minWidth:'130px'}}>
                            <Input
                              value={row.barcode || ''}
                              onChange={e => updateRow(row.id, { barcode: e.target.value })}
                              placeholder="Scan or type…"
                              className="h-8 text-sm font-mono"
                              data-testid={`qr-barcode-${row.id}`}
                            />
                          </td>

                          {/* Delete row */}
                          <td className="px-1 py-1.5 w-8">
                            {qrRows.length > 1 && (
                              <button onClick={() => removeRow(row.id)}
                                className="p-1 text-slate-300 hover:text-red-500 transition-colors rounded">
                                <Trash2 size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer actions */}
              <div className="shrink-0 flex items-center justify-between pt-3 border-t border-slate-200 mt-3">
                <Button variant="outline" size="sm" onClick={() => setQrRows(r => [...r, newRow()])} data-testid="qr-add-row">
                  <Plus size={14} className="mr-1.5" /> Add Row
                </Button>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">{qrRows.length} row{qrRows.length !== 1 ? 's' : ''}</span>
                  <Button variant="outline" onClick={() => setQrOpen(false)}>Cancel</Button>
                  <Button onClick={handleBatchGenerate} disabled={qrGenerating}
                    className="bg-amber-600 hover:bg-amber-700 text-white min-w-36"
                    data-testid="qr-generate-all-btn">
                    {qrGenerating ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Generating...</>
                    ) : (
                      <><Zap size={15} className="mr-1.5" />Generate {qrRows.length} Repack{qrRows.length !== 1 ? 's' : ''}</>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* TOTP Verification Dialog for Inventory Correction */}
      <TotpVerifyDialog
        open={totpOpen}
        onOpenChange={v => { setTotpOpen(v); if (!v) setPendingCorrection(null); }}
        context={`Inventory correction for ${editing?.name} in ${currentBranch?.name}`}
        title="Verify Admin Action"
        onVerified={({ manager_name, mode_used }) => {
          if (pendingCorrection) {
            submitCorrection(manager_name, mode_used);
          }
        }}
      />
      <UploadQRDialog
        open={corrUploadQROpen}
        onClose={(count) => { setCorrUploadQROpen(false); if (count > 0) toast.success(`${count} photo(s) attached to correction.`); }}
        recordType="inventory_correction"
        recordId={corrUploadId}
      />

      {/* Manage Categories Dialog */}
      <Dialog open={catMgmtOpen} onOpenChange={v => { setCatMgmtOpen(v); if (!v) setCatNewName(''); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold" style={{ fontFamily: 'Manrope' }}>
              <Tag size={17} className="text-violet-500" /> Manage Product Categories
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Categories are shared across all products in your company. Existing categories from your products are shown automatically.</p>
            {/* Add new */}
            <div className="flex gap-2">
              <input
                className="flex-1 h-9 border border-slate-200 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                placeholder="New category name"
                value={catNewName}
                onChange={e => setCatNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addCatMgmt(); }}
                data-testid="cat-mgmt-new-input"
              />
              <Button size="sm" onClick={addCatMgmt} disabled={catSaving || !catNewName.trim()} className="h-9 bg-violet-600 hover:bg-violet-700 text-white" data-testid="cat-mgmt-add-btn">
                {catSaving ? '…' : <><Plus size={13} className="mr-1" />Add</>}
              </Button>
            </div>
            {/* Category list */}
            <div className="max-h-64 overflow-y-auto space-y-1.5">
              {catMgmtList.length === 0 && (
                <p className="text-xs text-slate-400 italic text-center py-4">No categories yet. Add one above.</p>
              )}
              {catMgmtList.map(cat => (
                <div key={cat} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 border border-slate-100" data-testid={`cat-row-${cat}`}>
                  <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <Tag size={12} className="text-violet-400" /> {cat}
                  </span>
                  <button
                    onClick={() => deleteCatMgmt(cat)}
                    className="text-slate-300 hover:text-red-500 transition-colors"
                    title={`Delete "${cat}"`}
                    data-testid={`cat-delete-${cat}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <StockInjectionDialog
        open={injectionOpen}
        onOpenChange={setInjectionOpen}
        product={injectionProduct}
        onDone={() => { fetchProducts(); }}
      />
    </div>
  );
}
